import { useEffect, useRef, type RefObject } from "react";
import { getTabStops, rememberOpener, trapFocus } from "../utils/focusTrap";

export type DialogFocusOptions = {
  /** Runs on Escape. Point it at the dialog's existing cancel path so sensitive state is cleared the same way. */
  onClose: () => void;
  /** Element focused on open; defaults to the first focusable element inside the dialog. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** `false` while the dialog is submitting or running a job: Escape is then swallowed instead of closing. */
  canClose?: boolean;
  /** `false` while a component that is always mounted keeps its dialog closed. Defaults to `true`. */
  enabled?: boolean;
};

/** What the keydown handler reads; React and native keyboard events both satisfy it. */
export type DialogKeyEvent = Pick<KeyboardEvent, "key" | "shiftKey" | "defaultPrevented" | "preventDefault">;

/** The page-level seams, so the focus behaviour can run without a browser. */
export type DialogFocusHost = {
  getActiveElement: () => Element | null;
  /** Subscribes to key presses; returns the unsubscribe. */
  listen: (handler: (event: DialogKeyEvent) => void) => () => void;
  /** True when nothing holds focus (the page body), e.g. after the focused control left the DOM. */
  focusLost: () => boolean;
  /** Calls back after the container's subtree changes; returns the unsubscribe. */
  observe: (container: DialogContainer, callback: () => void) => () => void;
};

type DialogContainer = Pick<HTMLElement, "querySelectorAll" | "contains" | "getAttribute" | "focus">;
type DialogLiveOptions = Pick<DialogFocusOptions, "onClose" | "initialFocusRef" | "canClose">;

/** Open dialogs, oldest first. Only the last entry reacts to Escape and Tab. */
const openDialogs: symbol[] = [];

const browserHost = (): DialogFocusHost => ({
  getActiveElement: () => document.activeElement,
  // Window, not document: a `Dialog` opened above this one stops the event at the
  // document, so the lower dialog never sees the Escape that closed the upper one.
  listen: (handler) => {
    const listener = (event: KeyboardEvent) => handler(event);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  },
  focusLost: () => !document.activeElement || document.activeElement === document.body,
  observe: (container, callback) => {
    const observer = new MutationObserver(callback);
    // `container` is always a real element here; the narrow type only serves the test seam.
    observer.observe(container as unknown as Node, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled"],
    });
    return () => observer.disconnect();
  },
});

const firstFocusable = (container: DialogContainer): HTMLElement | null =>
  getTabStops(container)[0] ?? null;

/**
 * Binds the focus behaviour of one open dialog and returns the release function.
 * The hook below is the only React-facing entry point; this core is exported for tests.
 */
export const bindDialogFocus = (
  container: DialogContainer,
  read: () => DialogLiveOptions,
  host: DialogFocusHost = browserHost(),
): (() => void) => {
  const token = Symbol("dialog");
  const restoreOpener = rememberOpener(host.getActiveElement());
  openDialogs.push(token);

  const preferred = read().initialFocusRef?.current ?? null;
  preferred?.focus();
  // A disabled or detached preferred target takes no focus; fall back to the first control.
  if (!preferred || host.getActiveElement() !== preferred) firstFocusable(container)?.focus();

  // When the focused control leaves the dialog (a confirm footer swapped for progress,
  // a submit button replaced by a spinner) focus drops to the page body. Pull it back:
  // onto the panel when it is a focus target (tabindex), else the first control.
  const stopObserving = host.observe(container, () => {
    if (openDialogs[openDialogs.length - 1] !== token || !host.focusLost()) return;
    if (container.getAttribute("tabindex") !== null) container.focus();
    else firstFocusable(container)?.focus();
  });

  const stopListening = host.listen((event) => {
    if (openDialogs[openDialogs.length - 1] !== token) return;

    if (event.key === "Escape") {
      if (event.defaultPrevented) return;
      event.preventDefault();
      const { onClose, canClose } = read();
      if (canClose !== false) onClose();
      return;
    }

    if (event.key !== "Tab") return;
    const active = host.getActiveElement();
    // Focus that sits in another modal (a confirm above this one) is that modal's to trap.
    if (active?.closest?.('[aria-modal="true"]') && !container.contains(active)) return;
    const focusable = getTabStops(container);
    if (active && focusable.includes(active as HTMLElement)) {
      trapFocus(container, event, active);
      return;
    }
    // Inside the dialog on something that is not a tab stop (an unchecked radio, a tabindex -1 control): let Tab move naturally.
    if (active && active !== (container as unknown) && container.contains(active)) return;
    // Focus is on the panel itself, a backdrop, or a removed control: pull it back in.
    const target = event.shiftKey ? focusable[focusable.length - 1] : focusable[0];
    if (target) {
      event.preventDefault();
      target.focus();
    }
  });

  return () => {
    stopObserving();
    stopListening();
    const at = openDialogs.indexOf(token);
    if (at !== -1) openDialogs.splice(at, 1);
    restoreOpener();
  };
};

/**
 * Keyboard and focus behaviour for a hand-rolled overlay dialog: focus moves in on
 * open, Escape closes (unless `canClose` is `false`), Tab stays inside, and focus
 * returns to the control that opened it. Only the topmost open dialog reacts to
 * Escape and Tab, so a confirm above a modal closes alone.
 *
 * `containerRef` is the `role="dialog"` panel.
 */
export const useDialogFocus = (
  containerRef: RefObject<HTMLElement | null>,
  { onClose, initialFocusRef, canClose, enabled = true }: DialogFocusOptions,
): void => {
  // Latest options, read when a key arrives, so the bind effect below never re-runs on a new callback.
  const live = useRef<DialogLiveOptions>({ onClose, initialFocusRef, canClose });
  live.current = { onClose, initialFocusRef, canClose };

  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container) return undefined;
    return bindDialogFocus(container, () => live.current);
  }, [containerRef, enabled]);
};
